import { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";

import { invalidateAccountVerification } from "../services/accountVerification.memo";
import type { VerificationPurpose } from "../types/verification-api.types";
import {
  hasSeenVerificationWelcome,
  isVerificationWelcomeCandidate,
  markVerificationWelcomeSeen,
  verificationWelcomeKey,
} from "../utils/verificationWelcome";
import { useOrganizationVerificationStatus } from "./useOrganizationVerificationStatus";
import { useVerificationSocket } from "./useVerificationSocket";

export interface VerificationWelcomeGate {
  open: boolean;
  purpose: VerificationPurpose | null;
  /** Closes the welcome for good: this user is not shown it again for this identity in this browser. */
  dismiss: () => void;
}

/**
 * Whether the dashboard shell should announce the organization's approval, once.
 *
 * Approval usually arrives while the member is somewhere else: the capture went to review and the verdict
 * landed later. So the shell reads the same account verdict every other surface reads (the shared memo, no
 * new request on a warm cache) and listens for `verification:updated` on the notifications socket it already
 * holds (rule 10: no second socket). A push saying VERIFIED drops the memo and reads again, which is what
 * brings the approval stamp the welcome is gated on.
 *
 * The mark is written when the welcome is closed or one of its shortcuts is taken, not when it opens: a
 * reload before the reader acted shows it again, which is the point of telling them.
 */
export function useVerificationWelcomeGate(): VerificationWelcomeGate {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const { isOrgMember, data, identityId, purpose, refresh } = useOrganizationVerificationStatus();
  // Read once: the window is days long, so a session-long clock is precise enough and keeps render pure.
  const [mountedAt] = useState(() => Date.now());
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);

  useVerificationSocket(identityId, (payload) => {
    if (payload.status !== "VERIFIED") return;
    invalidateAccountVerification();
    refresh();
  });

  const key = user && identityId ? verificationWelcomeKey(user.id, identityId) : null;
  const open =
    key !== null &&
    key !== dismissedKey &&
    isVerificationWelcomeCandidate({ isOrgMember, data, now: mountedAt, pathname }) &&
    !hasSeenVerificationWelcome(key);

  const dismiss = useCallback(() => {
    if (!key) return;
    markVerificationWelcomeSeen(key);
    setDismissedKey(key);
  }, [key]);

  return { open, purpose, dismiss };
}
